import { useState, useEffect } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { DayPicker } from "react-day-picker";
import { fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import "react-day-picker/dist/style.css";
import {
  getEndDateFromPreset,
  getPeriodPresets,
  getStartDateFromPreset,
  monthsToPreset,
  PRESET_MONTH_VALUES,
  presetToMonths,
} from "./periodFilterPresets";

const dateToMonths = (date) => {
  if (!date) return null;
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30);
  return Math.ceil(diffMonths);
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const PeriodFilterModal = ({
  isOpen,
  onClose,
  onApply,
  currentPeriodFilter,
  currentPeriodStart,
  currentPeriodEnd,
}) => {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState("last6months");
  const [selectedRange, setSelectedRange] = useState({
    from: null,
    to: null,
  });
  const [selectingStart, setSelectingStart] = useState(true);
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .rdp-day_range_single_day {
        background: transparent !important;
      }
      .rdp-day_range_single_day .rdp-day_button {
        background-color: #2AB9B8 !important;
        color: white !important;
        border-radius: 50% !important;
      }
      .rdp-day_range_start {
        background: linear-gradient(to right, transparent 50%, rgba(42, 185, 184, 0.25) 50%) !important;
        border-radius: 0 !important;
      }
      .rdp-day_range_start .rdp-day_button {
        background-color: #2AB9B8 !important;
        color: white !important;
        border-radius: 50% !important;
      }
      .rdp-day_range_end {
        background: linear-gradient(to right, rgba(42, 185, 184, 0.25) 50%, transparent 50%) !important;
        border-radius: 0 !important;
      }
      .rdp-day_range_end .rdp-day_button {
        background-color: #2AB9B8 !important;
        color: white !important;
        border-radius: 50% !important;
      }
      .rdp-day_range_middle {
        background-color: rgba(42, 185, 184, 0.25) !important;
      }
      .char-period-filter-modal.modal,
      .char-period-filter-modal.modal.show {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 100vh !important;
        overflow-y: auto !important;
        padding: 1rem 0 !important;
      }
      .char-period-filter-modal .modal-content {
        width: 624px !important;
        height: 560px !important;
        max-width: 624px !important;
        max-height: 560px !important;
        min-width: 624px !important;
        min-height: 560px !important;
        border: 12px solid white !important;
      }
      .char-period-filter-modal .modal-dialog {
        margin: 0.5rem auto !important;
        align-self: center !important;
      }
      .char-preset-button {
        border-radius: 4px !important;
        font-weight: normal !important;
      }
      .char-preset-button-active {
        font-weight: bold !important;
      }
      .char-preset-list {
        max-width: fit-content !important;
        min-width: 12.5rem !important;
        padding-left: 1.25rem !important;
        padding-right: 0.5rem !important;
        margin-right: 0 !important;
      }
      .char-period-filter-modal .char-modal-body > div {
        gap: 4rem !important;
      }
      .rdp-caption {
        font-weight: normal !important;
      }
      .rdp-caption_label {
        font-weight: normal !important;
        text-transform: capitalize !important;
      }
      .rdp-weekdays,
      .rdp-head_row {
        margin-bottom: 1.5rem !important;
      }
      .rdp-weekday,
      .rdp-head_cell {
        padding-bottom: 0.5rem !important;
      }
      .rdp-month_grid thead tr,
      .rdp-table thead tr {
        margin-bottom: 1.5rem !important;
        display: table-row !important;
      }
      .rdp-month_grid thead,
      .rdp-table thead {
        margin-bottom: 1.5rem !important;
      }
      .rdp-month_grid tbody tr:first-child td,
      .rdp-table tbody tr:first-child td {
        padding-top: 0.1rem !important;
      }
      .char-modal-body .rdp {
        --rdp-accent-color: #9ca3af !important;
        --rdp-today-color: #2AB9B8 !important;
      }
      .char-modal-body .char-period-calendar .rdp-nav,
      .char-modal-body .char-period-calendar .rdp-nav_button,
      .char-modal-body .char-period-calendar .rdp-button_previous,
      .char-modal-body .char-period-calendar .rdp-button_next,
      .char-modal-body .char-period-calendar .rdp-caption button {
        color: #9ca3af !important;
      }
      .char-modal-body .char-period-calendar .rdp-nav_button svg,
      .char-modal-body .char-period-calendar .rdp-button_previous svg,
      .char-modal-body .char-period-calendar .rdp-button_next svg,
      .char-modal-body .char-period-calendar .rdp-caption svg,
      .char-modal-body .char-period-calendar .rdp-nav svg,
      .char-modal-body .char-period-calendar .rdp-caption button svg,
      .char-modal-body .char-period-calendar .rdp-nav button svg,
      .char-modal-body .char-period-calendar .rdp-chevron,
      .char-modal-body .char-period-calendar .rdp-chevron svg {
        fill: #9ca3af !important;
        stroke: #9ca3af !important;
        color: #9ca3af !important;
      }
      .char-modal-body .char-period-calendar .rdp-today:not(.rdp-outside),
      .char-modal-body .char-period-calendar .rdp-today:not(.rdp-outside) .rdp-day_button {
        color: #2AB9B8 !important;
        font-weight: 600 !important;
      }
      .char-modal-body .char-period-calendar .rdp-day_range_single_day.rdp-today .rdp-day_button,
      .char-modal-body .char-period-calendar .rdp-day_range_start.rdp-today .rdp-day_button,
      .char-modal-body .char-period-calendar .rdp-day_range_end.rdp-today .rdp-day_button {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const isPresetPeriod =
    currentPeriodFilter === null ||
    currentPeriodFilter === undefined ||
    PRESET_MONTH_VALUES.includes(Number(currentPeriodFilter));

  useEffect(() => {
    if (isOpen) {
      setSelectingStart(true);
      if (currentPeriodStart && currentPeriodEnd) {
        const presetFromMonths =
          currentPeriodFilter != null && currentPeriodFilter !== undefined
            ? monthsToPreset(currentPeriodFilter)
            : currentPeriodFilter === null || currentPeriodFilter === undefined
              ? "sinceBeginning"
              : null;
        if (presetFromMonths && isPresetPeriod) {
          setSelectedPreset(presetFromMonths);
          const startDate = getStartDateFromPreset(presetFromMonths);
          const endDate = getEndDateFromPreset(presetFromMonths);
          setSelectedRange({ from: startDate, to: endDate });
          setMonth(startDate);
        } else {
          setSelectedPreset(null);
          setSelectedRange({
            from: new Date(currentPeriodStart),
            to: new Date(currentPeriodEnd),
          });
          setMonth(new Date(currentPeriodStart));
        }
        return;
      }
      const preset = monthsToPreset(currentPeriodFilter);
      setSelectedPreset(preset);
      const startDate = getStartDateFromPreset(preset);
      const endDate = getEndDateFromPreset(preset);
      if (startDate) {
        setSelectedRange({
          from: startDate,
          to: endDate,
        });
        setMonth(startDate);
      } else {
        if (currentPeriodFilter !== null && currentPeriodFilter !== undefined) {
          const months = Number(currentPeriodFilter);
          if (!isNaN(months) && months > 0) {
            const now = new Date();
            const calculatedDate = new Date(now);
            if (months < 1) {
              const days = Math.ceil(months * 30);
              calculatedDate.setDate(calculatedDate.getDate() - days);
            } else {
              calculatedDate.setMonth(
                calculatedDate.getMonth() - Math.ceil(months),
              );
            }
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            setSelectedRange({
              from: calculatedDate,
              to: today,
            });
            setMonth(calculatedDate);
          } else {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            setSelectedRange({
              from: new Date(),
              to: today,
            });
            setMonth(new Date());
          }
        } else {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          setSelectedRange({
            from: new Date(),
            to: today,
          });
          setMonth(new Date());
        }
      }
    }
  }, [
    isOpen,
    currentPeriodFilter,
    currentPeriodStart,
    currentPeriodEnd,
    isPresetPeriod,
  ]);
  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId);
    setSelectingStart(true);
    const startDate = getStartDateFromPreset(presetId);
    const endDate = getEndDateFromPreset(presetId);

    if (startDate) {
      setSelectedRange({
        from: startDate,
        to: endDate,
      });
      setMonth(startDate);
    }
  };
  const handleDayClick = (date) => {
    setSelectedPreset(null);

    if (selectingStart) {
      setSelectedRange({
        from: date,
        to: null,
      });
      setSelectingStart(false);
      setMonth(date);
    } else {
      if (selectedRange.from) {
        if (date < selectedRange.from) {
          setSelectedRange({
            from: date,
            to: selectedRange.from,
          });
        } else {
          setSelectedRange({
            from: selectedRange.from,
            to: date,
          });
        }
        setSelectingStart(true);
      }
    }
  };
  const handleApply = () => {
    let months = null;
    let label = "";
    let startDate = null;
    let endDate = null;
    let presetId = null;

    if (selectedPreset) {
      presetId = selectedPreset;
      months = presetToMonths(selectedPreset);
      const preset = getPeriodPresets(t).find((p) => p.id === selectedPreset);
      label = preset ? preset.label : "";
      startDate = startOfDay(getStartDateFromPreset(selectedPreset));
      endDate = endOfDay(getEndDateFromPreset(selectedPreset));
    } else if (selectedRange.from && selectedRange.to) {
      const fromDate = selectedRange.from;
      const toDate = selectedRange.to;
      months = dateToMonths(fromDate);
      startDate = startOfDay(fromDate);
      endDate = endOfDay(toDate);

      const fromStr = fromDate.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const toStr = toDate.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      label = `${fromStr} - ${toStr}`;
    } else if (selectedRange.from) {
      const singleDate = selectedRange.from;
      startDate = startOfDay(singleDate);
      endDate = endOfDay(singleDate);
      months = dateToMonths(singleDate);
      label = singleDate.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    if (startDate && endDate) {
      onApply(
        months,
        label,
        startOfDay(startDate).toISOString(),
        endOfDay(endDate).toISOString(),
        presetId,
      );
    } else {
      onApply(months, label, null, null, presetId);
    }
    onClose();
  };
  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="char-period-filter-modal"
      contentClassName="rounded-lg"
      style={{
        width: "624px",
        height: "560px",
        maxWidth: "624px",
        maxHeight: "560px",
        minWidth: "624px",
        minHeight: "560px",
      }}
      centered
    >
      <ModalHeader
        className="char-modal-header"
        close={
          <button
            type="button"
            onClick={onClose}
            className="char-modal-header-close"
            aria-label={t("Close")}
          >
            <i className="fas fa-times char-modal-header-close-icon" />
          </button>
        }
      >
        <span className="char-modal-header-title">{t("Period")}</span>
      </ModalHeader>
      <ModalBody
        className="char-modal-body"
        style={{
          height: "calc(560px - 140px)",
          overflow: "auto",
          padding: "1rem",
        }}
      >
        <div className="flex gap-6 h-full">
          <div className="char-preset-list">
            {getPeriodPresets(t).map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePresetSelect(preset.id)}
                className={`char-preset-button ${
                  selectedPreset === preset.id
                    ? "char-preset-button-active"
                    : "char-preset-button-inactive"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex-1 char-period-calendar">
            <DayPicker
              selected={selectedRange.from}
              onDayClick={handleDayClick}
              month={month}
              onMonthChange={setMonth}
              disabled={{ after: new Date() }}
              locale={fr}
              weekStartsOn={1}
              modifiers={{
                selected: selectedRange.from ? [selectedRange.from] : [],
                range_single_day:
                  selectedRange.from &&
                  (!selectedRange.to ||
                    selectedRange.from.toDateString() ===
                      selectedRange.to.toDateString())
                    ? [selectedRange.from]
                    : [],
                range_start:
                  selectedRange.from &&
                  selectedRange.to &&
                  selectedRange.from.toDateString() !==
                    selectedRange.to.toDateString()
                    ? [
                        selectedRange.from < selectedRange.to
                          ? selectedRange.from
                          : selectedRange.to,
                      ]
                    : [],
                range_end:
                  selectedRange.from &&
                  selectedRange.to &&
                  selectedRange.from.toDateString() !==
                    selectedRange.to.toDateString()
                    ? [
                        selectedRange.from < selectedRange.to
                          ? selectedRange.to
                          : selectedRange.from,
                      ]
                    : [],
                range_middle:
                  selectedRange.from && selectedRange.to
                    ? (() => {
                        const start =
                          selectedRange.from < selectedRange.to
                            ? selectedRange.from
                            : selectedRange.to;
                        const end =
                          selectedRange.from < selectedRange.to
                            ? selectedRange.to
                            : selectedRange.from;
                        const daysDiff = Math.floor(
                          (end - start) / (1000 * 60 * 60 * 24),
                        );
                        if (daysDiff <= 1) return [];
                        return Array.from({ length: daysDiff - 1 }, (_, i) => {
                          const date = new Date(start);
                          date.setDate(date.getDate() + i + 1);
                          return date;
                        });
                      })()
                    : [],
              }}
              modifiersClassNames={{
                selected: "rdp-day_selected",
                range_single_day: "rdp-day_range_single_day",
                range_start: "rdp-day_range_start",
                range_end: "rdp-day_range_end",
                range_middle: "rdp-day_range_middle",
              }}
              formatters={{
                formatMonthCaption: (date) => {
                  const monthName = date.toLocaleDateString("fr-FR", {
                    month: "long",
                  });
                  const year = date.getFullYear();
                  const capitalizedMonth =
                    monthName.charAt(0).toUpperCase() + monthName.slice(1);
                  return `${capitalizedMonth} ${year}`;
                },
                formatWeekdayName: (date) => {
                  const weekdayName = date.toLocaleDateString("fr-FR", {
                    weekday: "short",
                  });
                  return weekdayName.charAt(0).toUpperCase();
                },
              }}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="char-modal-footer">
        <button type="button" onClick={handleCancel} className="char-button">
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="char-button char-button-primary"
        >
          {t("Apply")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default PeriodFilterModal;
